/**
 * Book a third-party (gig courier) delivery for the current draft run.
 *
 * Mirrors the Pending Orders tab's handleDispatch shape, minus the driver-dispatch
 * netlify call: the SHELL mobility_runs row is real (fulfillment_type
 * 'external_courier', driver/vehicle/profile null — the courier replaces all
 * three), the stop + order assignment are real, and the courier leg goes
 * through the ThirdPartyDeliveryClient seam (mock today; parts_deliveries
 * insert when the backend milestone lands).
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useRooftop, useUser } from '@store';
import { useAssignPartsOrdersMutation } from '../queries/assignPartsOrders.mutation.generated';
import { useInsertPartsRunMutation } from '../queries/insertPartsRun.mutation.generated';
import { useInsertPartsStopMutation } from '../queries/insertPartsStop.mutation.generated';
import { getThirdPartyClient } from '../thirdParty/client';
import { buildStopForDestination } from '../utils/stopBuilder';
import { VENDOR_LABELS, type TpPoint, type TpQuote } from '../thirdParty/types';

/** Pickup = the rooftop. Couriers need an address AND a pickup phone (Uber hard-requires it). */
export function buildPickupPoint(rooftop: any): { pickup: TpPoint | null; problem: string | null } {
  const address = rooftop?.location?.address || rooftop?.address || '';
  const phone = rooftop?.phone || rooftop?.location?.phone || '';
  console.log('rooftop', rooftop);
  console.log('address', address);
  console.log('phone', phone);
  if (!address) {
    return { pickup: null, problem: 'Your rooftop has no street address on file — add one in Settings before using couriers.' };
  }
  if (!phone) {
    return { pickup: null, problem: 'Couriers need a pickup phone number. Add a phone to your rooftop in Settings.' };
  }
  return {
    pickup: {
      name: rooftop?.name || 'Parts Counter',
      business_name: rooftop?.name || undefined,
      phone,
      address,
      lat: rooftop?.location?.latitude ?? undefined,
      lng: rooftop?.location?.longitude ?? undefined,
    },
    problem: null,
  };
}

export function useThirdPartyBooking(onBooked?: () => void) {
  const rooftop = useRooftop();
  const user = useUser();
  const navigate = useNavigate();
  const [booking, setBooking] = React.useState(false);

  const [createRun] = useInsertPartsRunMutation();
  const [createStop] = useInsertPartsStopMutation();
  const [assignOrders] = useAssignPartsOrdersMutation();

  const { pickup, problem: pickupProblem } = buildPickupPoint(rooftop);

  const book = async (
    draftRun: any,
    quote: TpQuote,
    opts?: { departureTime?: string; requireSignature?: boolean }
  ) => {
    const dest = (draftRun?.stops || [])[0];
    const orders = (dest?.delivery_orders || []) as any[];
    const customerId = rooftop?.id;
    if (!dest || !pickup || !customerId) {
      toast.warning('Select orders for one destination before booking a courier.');
      return;
    }
    const userEmail = user?.profile?.email || 'unknown';
    setBooking(true);
    try {
      // 1. Real shell run — the courier replaces driver/vehicle/POD workflow.
      const runRes = await createRun({
        variables: {
          run: {
            customer_id: customerId,
            product_type: 'parts_delivery',
            fulfillment_type: 'external_courier',
            status: 'dispatched',
            departure_time: opts?.departureTime || new Date().toISOString(),
            driver_id: null,
            vehicle_id: null,
            profile_id: null,
            template_id: null,
            created_by: userEmail,
            updated_by: userEmail,
          },
        },
      });
      const runId = runRes.data?.insert_mobility_runs_one?.id;
      if (!runId) throw new Error('Failed to create the run');

      // 2. The single destination stop + order assignment (existing flow).
      const stopRes = await createStop({ variables: { stop: buildStopForDestination(runId, dest, 1) } });
      const stopId = stopRes.data?.insert_mobility_stops_one?.id;
      const orderIds = orders.map(o => o.id);
      if (stopId && orderIds.length) {
        await assignOrders({ variables: { orderIds, runId, stopId } });
      }

      // 3. The courier leg through the seam. Per DoorDash's pickup/dropoff-info
      // guidance: the reference tag is what the courier matches against the
      // package on the counter (order numbers), and the orders' special
      // instructions ride as dropoff instructions.
      const referenceTag = [...new Set(orders.map(o => o.order_number).filter(Boolean))]
        .join(', ')
        .slice(0, 100);
      const dropoffNotes = [...new Set(orders.map(o => o.special_instructions).filter(Boolean))]
        .join('; ')
        .slice(0, 280);
      await getThirdPartyClient().book({
        run_id: runId,
        customer_id: customerId,
        created_by: userEmail,
        requested_vendor: quote.vendor,
        quote_id: quote.quote_id,
        pickup: { ...pickup, reference_tag: referenceTag || undefined },
        dropoff: {
          name: dest.recipient_name || dest.recipient_business || 'Recipient',
          business_name: dest.recipient_business || undefined,
          phone: dest.recipient_phone || undefined,
          address: dest.address || '',
          lat: dest.delivery_lat ?? undefined,
          lng: dest.delivery_lng ?? undefined,
          notes: dropoffNotes || undefined,
        },
        items: orders.map(o => ({ name: o.item_description || o.part_number || 'Part', quantity: o.quantity || 1 })),
        // Every parts booking captures a dropoff photo (the POD story); the
        // signature is the dealer's per-booking toggle.
        verification: {
          requires_photo: true,
          ...(opts?.requireSignature ? { requires_signature: true } : null),
        },
        ...(opts?.departureTime ? { ready_at: opts.departureTime } : null),
      });

      toast.success(`${VENDOR_LABELS[quote.vendor]} booked — Run #${runId}`);
      onBooked?.();
      navigate(`/parts-delivery/runs/${runId}`);
    } catch (error: any) {
      console.error('Failed to book third-party delivery:', error);
      toast.error(`Failed to book courier: ${error?.message || 'Unknown error'}`);
    } finally {
      setBooking(false);
    }
  };

  return { book, booking, pickup, pickupProblem };
}
